import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Mic, MicOff } from 'lucide-react';

/**
 * Full-screen bidirectional voice mode.
 *
 * Visually: a single big animated orb centered on the screen, transcripts
 * stacked above + below, and an explicit close button. Functionally: a
 * WebRTC RTCPeerConnection to OpenAI's Realtime API at
 * `https://api.openai.com/v1/realtime/calls?model=...`, authenticated with
 * a short-lived `ek_` ephemeral key minted by /api/voice/conversation-token.
 *
 * Audio in (the user) flows over the WebRTC audio track; audio out (the
 * model) lands on `<audio>` we attach to the remote track. Transcripts
 * arrive on the data channel as `conversation.item.input_audio_transcription.delta`
 * (user) and `response.audio_transcript.delta` (assistant) events.
 *
 * Props:
 *   open       — render-gate; the overlay only mounts/connects when true.
 *   onClose()  — called when user clicks X (or hits Escape).
 *   agentId    — currently active agent slug (passed to token endpoint so
 *                the model adopts that persona).
 *   agentName  — display label for the overlay header.
 */

/**
 * Mic input constraints. Sensible on desktop, ESSENTIAL on iOS Safari —
 * without these iPhones tend to (a) pick the bottom mic with heavy ambient
 * pickup, (b) run AGC hot so HVAC hum / speaker bleed becomes "noise the
 * server VAD trips on", and (c) deliver 48 kHz audio while the Realtime
 * session expects 24 kHz, forcing a downsample on the audio fast path.
 */
const VOICE_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  sampleRate: 24000,
  channelCount: 1,
};

export default function VoiceModeOverlay({ open, onClose, agentId, agentName, onTurn }) {
  const [status, setStatus] = useState('idle');       // idle | connecting | listening | speaking | error
  const [userTranscript, setUserTranscript] = useState('');
  const [assistantTranscript, setAssistantTranscript] = useState('');
  const [muted, setMuted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Hush mic for the first ~3 seconds after connect. iPhone / speakerphone
  // setups otherwise hear ambient noise (or the tail of the connection
  // chirp) before the greeting starts and route it through to the model
  // as a spurious user utterance, triggering an unwanted clarification.
  // Cleared automatically by the timer set in start(); the muted useEffect
  // combines this with the user's explicit mute toggle.
  const [hushOnConnect, setHushOnConnect] = useState(false);
  const hushTimerRef = useRef(null);

  // Hush mic while the filler narration is speaking. Without this, the
  // 2-3s of "I'll look up..." audio bleeds back into the mic on
  // speakerphone setups, gets transcribed, and triggers a spurious
  // second tool call ("Go ahead.", "Thanks.") — which then races with
  // the original turn's still-pending result. Barge-in during the FINAL
  // answer is preserved; only the brief filler window is hushed.
  const [hushDuringFiller, setHushDuringFiller] = useState(false);

  // Active-response serializer. Realtime rejects a second response.create
  // while ANY response is in flight ("conversation_already_has_active_
  // response"). That includes responses the MODEL started on its own
  // (e.g. when Whisper segments a continuing user utterance into a
  // follow-up turn and the session's tool_choice:required kicks off
  // another response). We track every response.created/done event so
  // our filler + final dispatches can defer behind whatever is currently
  // speaking instead of erroring out.
  const activeResponseIdRef = useRef(null);

  // Audio energy of the model's output, used to animate the orb.
  const [outputLevel, setOutputLevel] = useState(0);

  // ---- Debug panel ----
  // Logs every turn's routing so you can see at a glance whether voice mode
  // answered via the Jobotics Agent function call or directly from OpenAI.
  // Off by default — flip via the "Debug" pill in the header when investigating.
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugLog, setDebugLog] = useState([]);
  const [routeBadge, setRouteBadge] = useState(null); // 'agent' | 'direct' | null
  // Ref mirror of routeBadge so the data-channel message listener — which is
  // registered ONCE inside connect() and would otherwise capture a stale
  // state value — can read the current value at event-time. Without this,
  // the response.done handler for the model's SPOKEN response (which comes
  // AFTER a tool call returns) sees routeBadge=null and incorrectly flags
  // "OpenAI direct".
  const routeBadgeRef = useRef(null);
  const setRouteBadgeSynced = (v) => {
    routeBadgeRef.current = v;
    setRouteBadge(v);
  };
  const debugLog_ = (msg, kind = 'info') => {
    setDebugLog((prev) => [
      ...prev.slice(-50),
      { ts: new Date().toLocaleTimeString([], { hour12: false }), msg, kind, id: Math.random() },
    ]);
  };

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const micStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  // Rolling conversation history forwarded to /api/voice/agent-query and
  // /api/voice/clarification so the stateless backends can ground brief
  // follow-ups against earlier turns. Capped to last 12 turns server-side.
  const historyRef = useRef([]);
  const pendingUserUtteranceRef = useRef('');
  // Suppress back-to-back clarifications: once we asked one and the user
  // answered, the NEXT call should always speak the full agent answer, not
  // another clarification, even if the clarification endpoint suggests one.
  const justAskedClarificationRef = useRef(false);
  // When we choose to speak the clarification (fast path), the agent-query
  // is still running. We stash its promise here so a background `then` can
  // append its result to history once it lands — without blocking the spoken
  // clarification path.
  const pendingAgentPromiseRef = useRef(null);
  // Live partial filler state — consolidated into ONE ref so a single
  // resetFillerState() can wipe everything from every entry point.
  //
  // The agent-query SSE stream emits cumulative `partial` text events as
  // Haiku narrates ("I'll look up the MCAA labor table for 100 LF of
  // 2-inch copper..."). We speak the FIRST stable partial as audible
  // filler so the user hears progress within ~2s instead of staring at
  // silence for the full 3-15s tool window. The final answer is dispatched
  // separately when the `result` event lands.
  //
  // Field semantics:
  //   committed       — false until clarification resolves null. The
  //                     stability timer is a no-op before this flips, so
  //                     we never race the clarification-wins fast path.
  //   spoken          — dispatch filler AT MOST ONCE per user turn.
  //   viaCall         — filler consumed function_call_output, so the final
  //                     answer must use the instructions-only response.create
  //                     path (you can't send a second function_call_output
  //                     for the same call_id).
  //   active          — a spoken response is in flight. Realtime rejects
  //                     a second response.create while one is active
  //                     ("conversation_already_has_active_response"), so
  //                     the final-answer dispatch must wait.
  //   pendingDispatch — the queued final-answer dispatch fn, drained by
  //                     the response.done handler when filler finishes.
  //   finalReady      — set when `result` SSE event arrives. Late-firing
  //                     stability timers skip filler if this is true —
  //                     no point narrating "I'll look that up" when the
  //                     answer is already here.
  //   partial         — most-recent cumulative partial text (within turn).
  //   stableTimer     — setTimeout that fires 500ms after the last growth.
  //   callId          — current ask_jobotics_agent call_id for filler
  //                     dispatch.
  const fillerRef = useRef({
    committed: false,
    spoken: false,
    viaCall: false,
    active: false,
    pendingDispatch: null,
    partial: '',
    stableTimer: null,
    callId: null,
    finalReady: false,
    abortCtrl: null, // AbortController for the in-flight agent-query fetch
  });
  const resetFillerState = (callId = null) => {
    const f = fillerRef.current;
    if (f.stableTimer) clearTimeout(f.stableTimer);
    // Cancel any in-flight agent-query / clarification from the previous
    // turn. Without this, a late-arriving result from a stale fetch tries
    // to dispatch into the CURRENT turn's call_id and either errors out
    // ("conversation_already_has_active_response") or — worse — succeeds
    // and the user hears the OLD answer for the NEW question.
    if (f.abortCtrl) {
      try { f.abortCtrl.abort(); } catch (_) { /* noop */ }
    }
    f.abortCtrl = null;
    f.committed = false;
    f.spoken = false;
    f.viaCall = false;
    f.active = false;
    f.pendingDispatch = null;
    f.partial = '';
    f.stableTimer = null;
    f.callId = callId;
    f.finalReady = false;
  };
  // Pairing state for the onTurn callback that pushes voice turns into the
  // parent's chat history. We capture the user's transcribed utterance when
  // it's finalized, accumulate the assistant transcript chunks, and fire
  // onTurn when the assistant audio completes.
  const lastUserUtteranceForPairingRef = useRef('');
  const assistantTranscriptForPairingRef = useRef('');
  const onTurnRef = useRef(onTurn);
  useEffect(() => { onTurnRef.current = onTurn; }, [onTurn]);

  // ---------- teardown ----------
  const tearDown = useCallback(() => {
    try { dcRef.current?.close?.(); } catch (_) { /* noop */ }
    dcRef.current = null;
    try {
      pcRef.current?.getSenders?.().forEach((s) => {
        try { s.track?.stop?.(); } catch (_) { /* noop */ }
      });
    } catch (_) { /* noop */ }
    try { pcRef.current?.close?.(); } catch (_) { /* noop */ }
    pcRef.current = null;
    try { micStreamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    micStreamRef.current = null;
    if (remoteAudioRef.current) {
      try { remoteAudioRef.current.srcObject = null; } catch (_) { /* noop */ }
    }
    try { audioCtxRef.current?.close?.(); } catch (_) { /* noop */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (hushTimerRef.current) {
      clearTimeout(hushTimerRef.current);
      hushTimerRef.current = null;
    }
    // Wipe filler state so late-firing timers can't dispatch after the
    // user has already closed voice mode.
    resetFillerState();
    activeResponseIdRef.current = null;
    setHushOnConnect(false);
    setHushDuringFiller(false);
    setOutputLevel(0);
  }, []);

  // ---------- connect ----------
  const connect = useCallback(async () => {
    setStatus('connecting');
    setErrorMsg('');
    setUserTranscript('');
    setAssistantTranscript('');
    historyRef.current = [];
    pendingUserUtteranceRef.current = '';
    justAskedClarificationRef.current = false;

    try {
      // 1. Mint ephemeral conversation token (server uses agent prompt prefix).
      const tokRes = await fetch('/api/voice/conversation-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ agentId: agentId || '' }),
      });
      if (!tokRes.ok) throw new Error(`token http ${tokRes.status}`);
      const { clientSecret, model } = await tokRes.json();

      // 2. Mic + peer connection.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: VOICE_AUDIO_CONSTRAINTS });
      micStreamRef.current = stream;
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.addTrack(stream.getAudioTracks()[0], stream);

      // Hush the mic for the first ~3 seconds. Two reasons:
      //   (a) On iPhone speakerphone, the connection chirp + room ambience
      //       in the first few hundred ms often gets transcribed as a
      //       spurious user utterance ("uh", "hey?", "are you there?")
      //       which then triggers an unwanted clarification turn.
      //   (b) The agent greeting itself plays out in the first 2-3s; if
      //       the mic stays hot during that window, semantic VAD can mis-
      //       attribute the greeting tail to the user.
      // We unmute after 2800ms which covers a typical 18-word greeting.
      // The user's explicit mute toggle remains independent.
      stream.getAudioTracks().forEach((t) => { t.enabled = false; });
      setHushOnConnect(true);
      if (hushTimerRef.current) clearTimeout(hushTimerRef.current);
      hushTimerRef.current = setTimeout(() => {
        setHushOnConnect(false);
        hushTimerRef.current = null;
      }, 2800);

      // Audio out: pipe the model's remote track into our <audio> element AND
      // into a Web Audio analyser so we can animate the orb.
      pc.ontrack = (event) => {
        const [remoteStream] = event.streams;
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remoteStream;
          // Browsers require an explicit play() in some autoplay-restricted
          // contexts; ignore the promise rejection if it fires.
          try { remoteAudioRef.current.play?.()?.catch?.(() => { /* noop */ }); } catch (_) { /* noop */ }
        }
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx && !audioCtxRef.current) {
            const ctx = new Ctx();
            audioCtxRef.current = ctx;
            const src = ctx.createMediaStreamSource(remoteStream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            src.connect(analyser);
            analyserRef.current = analyser;
            const buf = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
              if (!analyserRef.current) return;
              analyserRef.current.getByteTimeDomainData(buf);
              // Compute RMS energy [0..1].
              let sumSq = 0;
              for (let i = 0; i < buf.length; i += 1) {
                const v = (buf[i] - 128) / 128;
                sumSq += v * v;
              }
              const rms = Math.sqrt(sumSq / buf.length);
              setOutputLevel(rms);
              rafRef.current = requestAnimationFrame(tick);
            };
            tick();
          }
        } catch (_) { /* analyser is best-effort */ }
      };

      // Data channel for control + transcription events.
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      // Trigger an immediate, agent-aware intro the moment the data channel
      // opens. Without this the orb sits silent until the user speaks, which
      // makes voice mode feel slow on first connect. tool_choice: 'none' is
      // required for this one response because the session is configured
      // with tool_choice: 'required' (every user-driven response must call
      // ask_jobotics_agent) — we want the model to speak directly here, not
      // route to the agent backend.
      const personaName = (agentName || 'Jobotics Agent').replace(/ Agent$/, '');
      dc.addEventListener('open', () => {
        try {
          dc.send(JSON.stringify({
            type: 'response.create',
            response: {
              tool_choice: 'none',
              instructions:
                `Greet the user warmly in ONE short sentence as the ${agentName || 'Jobotics Agent'}. ` +
                `Mention what you specialize in (your domain — ${personaName}) and invite them to ask. ` +
                `Keep it under 18 spoken words. Examples: ` +
                `"Hi! I'm your Labor Estimator — ready to size labor hours for any MEP scope. What are we pricing?" ` +
                `"Hey there! Schedule Agent here — I can look at float, critical-path risk, and recovery. What needs attention?" ` +
                `Do NOT call any tool. Just speak the greeting.`,
            },
          }));
        } catch (_) { /* noop */ }
      });

      // Accumulator for incremental function-call arguments. The Realtime API
      // streams the arguments JSON via `response.function_call_arguments.delta`
      // and finalizes with `response.function_call_arguments.done`. We need
      // to dispatch on done because deltas arrive in arbitrary fragments.
      const fnArgsByCallId = {};

      dc.addEventListener('message', (e) => {
        let event;
        try { event = JSON.parse(e.data); } catch (_) { return; }
        const t = event.type || '';

        // User speech transcript (streaming during their utterance).
        if (t === 'conversation.item.input_audio_transcription.delta') {
          setUserTranscript((prev) => prev + (event.delta || ''));
          setStatus('listening');
          return;
        }
        if (t === 'conversation.item.input_audio_transcription.completed') {
          if (event.transcript) {
            setUserTranscript(event.transcript);
            debugLog_(`🎤 user: "${event.transcript}"`, 'user');
            pendingUserUtteranceRef.current = event.transcript;
            // Hold the user's utterance for pairing with the assistant
            // response when it completes. This is what we push to the
            // parent's chat history via onTurn.
            lastUserUtteranceForPairingRef.current = event.transcript;
            assistantTranscriptForPairingRef.current = '';
            historyRef.current = [
              ...historyRef.current,
              { role: 'user', content: event.transcript },
            ].slice(-12);
          }
          return;
        }

        // Assistant speech transcript (streaming as the model speaks).
        if (t === 'response.audio_transcript.delta' || t === 'response.output_audio_transcript.delta') {
          setAssistantTranscript((prev) => prev + (event.delta || ''));
          // Mirror into the pairing ref so onTurn fires with the complete
          // text even if state updates are batched.
          assistantTranscriptForPairingRef.current += event.delta || '';
          setStatus('speaking');
          return;
        }
        if (t === 'response.audio_transcript.done' || t === 'response.output_audio_transcript.done') {
          setStatus('listening');
          // Pair this assistant response with the most recent user utterance
          // and push to the parent's chat-history surface. Voice turns now
          // appear in the JoboticsAgent chat thread alongside text turns.
          const userText = lastUserUtteranceForPairingRef.current;
          const finalAssistant = (event.transcript && event.transcript.trim())
            || assistantTranscriptForPairingRef.current.trim();
          if (onTurnRef.current && userText && finalAssistant) {
            try {
              onTurnRef.current({
                user: userText,
                assistant: finalAssistant,
                agentId: agentId || null,
              });
            } catch (turnErr) {
              console.warn('[VoiceModeOverlay] onTurn callback threw:', turnErr?.message);
            }
          }
          lastUserUtteranceForPairingRef.current = '';
          assistantTranscriptForPairingRef.current = '';
          return;
        }
        if (t === 'input_audio_buffer.speech_started') {
          setAssistantTranscript('');
          setUserTranscript('');
          setRouteBadgeSynced(null);
          setStatus('listening');
          debugLog_('🎙️ speech started', 'meta');
          return;
        }
        if (t === 'response.created') {
          // Capture every response.created — both ours (filler / final /
          // greeting) and the model's auto-responses. The id is just used
          // as a truthy "active" sentinel; we don't gate by specific id.
          activeResponseIdRef.current = event.response?.id || 'unknown';
          setStatus('speaking');
          return;
        }

        // Function tool: ask_jobotics_agent
        if (t === 'response.function_call_arguments.delta') {
          const id = event.call_id || event.callId;
          if (!id) return;
          if (!fnArgsByCallId[id]) {
            console.log('[VoiceModeOverlay] 🔧 function call starting: ask_jobotics_agent (call_id:', id, ')');
          }
          fnArgsByCallId[id] = (fnArgsByCallId[id] || '') + (event.delta || '');
          return;
        }
        if (t === 'response.function_call_arguments.done') {
          const id = event.call_id || event.callId;
          const name = event.name;
          if (!id || name !== 'ask_jobotics_agent') {
            console.warn('[VoiceModeOverlay] function_call_arguments.done with unexpected name/id:', name, id);
            debugLog_(`❓ unexpected function call: name=${name} id=${id}`, 'warn');
            return;
          }
          const argsRaw = event.arguments || fnArgsByCallId[id] || '';
          delete fnArgsByCallId[id];
          let parsed = {};
          try { parsed = JSON.parse(argsRaw); } catch (_) { /* fall through with empty */ }
          console.log('[VoiceModeOverlay] 🔧 function call ready, question:', JSON.stringify(parsed.question));
          debugLog_(`🔧 calling ask_jobotics_agent("${parsed.question || ''}")`, 'agent');
          setRouteBadgeSynced('agent');
          setStatus('connecting');

          // Zero per-turn filler state. Refs persist across closures, so
          // without this a stale "spoken=true" from the previous turn
          // would silently route the next answer down the instructions-
          // only path instead of function_call_output.
          resetFillerState(id);

          // Speak the current stable partial as filler narration so the
          // user hears progress within ~2s instead of staring at silence
          // for the 3-15s tool window. Called from two places: the SSE
          // stability timer (500ms after last growth) and synchronously
          // right after clarification resolves null (in case a stable
          // partial already exists at that moment). Speaks at most ONCE
          // per turn — guarded by fillerRef.spoken.
          const maybeSpeakFiller = () => {
            const f = fillerRef.current;
            // Guards (any failing → skip filler entirely):
            //   committed   — we're in the agent path (not clarification wins)
            //   !spoken     — haven't already spoken filler this turn
            //   !finalReady — the real answer isn't already in hand
            //   text >= 20  — partial is substantive, not a single token
            //   callId      — the call to attach the filler to
            //   no active   — no other response is in flight (Whisper-
            //                 segmented continuing speech can cause the
            //                 model to auto-create a response, and Realtime
            //                 will reject our filler if we dispatch on top)
            const rawText = (f.partial || '').trim();
            if (!f.committed || f.spoken || f.finalReady || rawText.length < 20 || !f.callId) return;
            if (activeResponseIdRef.current) {
              debugLog_('⏭ skipping filler — another response is active', 'meta');
              return;
            }
            // CAP FILLER LENGTH. If the agent's pre-tool narration includes
            // fabricated numbers ("Base labor is 18 hr/100 ft, productivity
            // 1.15, $4,554..."), surfacing the whole partial speaks those
            // wrong numbers to the user — then the real MCAA answer plays
            // afterward. Two contradictory answers = bad UX. Cap to the
            // first sentence (period/?/!) or 120 chars, whichever lands
            // first. The agent's system prompt also forbids pre-tool
            // numeric speculation as a backstop, but this trim is defense
            // in depth in case it leaks through.
            const firstSentence = rawText.match(/^[^.?!]{1,120}[.?!]/);
            const text = firstSentence
              ? firstSentence[0].trim()
              : rawText.slice(0, 120).replace(/\s+\S*$/, '') + '…';

            f.spoken = true;
            f.viaCall = true;
            f.active = true;
            // Hush the mic for the filler window — the model's own voice
            // bleeding into the speaker → mic loop was causing spurious
            // "Thanks." / "Go ahead." tool calls that then raced the real
            // answer to dispatch. Mic re-opens on filler's response.done.
            setHushDuringFiller(true);
            debugLog_(`🔊 filler: "${text.slice(0, 80)}${text.length > 80 ? '…' : ''}"`, 'agent');
            try {
              dc.send(JSON.stringify({
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: f.callId,
                  output: JSON.stringify({ answer: text, isProgressNarration: true }),
                },
              }));
              dc.send(JSON.stringify({
                type: 'response.create',
                response: {
                  tool_choice: 'none',
                  instructions:
                    'Speak the function_call_output `answer` field VERBATIM as ONE brief progress sentence — this is the agent telling the user what it is currently looking up. Do NOT add commentary. Do NOT call any tool. Do NOT extend, paraphrase, or continue past the provided text. Stop at the end of the answer field.',
                },
              }));
            } catch (sendErr) {
              console.warn('[VoiceModeOverlay] filler dispatch failed:', sendErr?.message);
              f.spoken = false;
              f.viaCall = false;
              f.active = false;
              setHushDuringFiller(false);
            }
          };

          (async () => {
            try {
              const priorHistory = historyRef.current.slice(0, -1);
              const payload = {
                question: parsed.question || '',
                agentId: agentId || '',
                history: priorHistory,
              };
              // RACE pattern (replaces the prior Promise.all): we want to
              // speak the FIRST useful result, not wait for both. Clarification
              // is fast (~500ms); agent-query through the full Jobotics Agent
              // Quick workflow can be 5-15s. The prior Promise.all forced
              // every voice turn to wait for the slow agent before speaking
              // anything — exactly the lag the user complained about.
              //
              // New flow:
              //   1. Fire both immediately.
              //   2. Await clarification. If it returns a question (and we
              //      haven't just asked one), speak it RIGHT NOW. The slower
              //      agent-query keeps running; we stash its promise so the
              //      next turn (with the user's reply) has prior analysis.
              //   3. If clarification is null, wait for agent-query.
              // AbortController for this turn. resetFillerState() at the
              // start of the NEXT turn calls .abort() on this controller,
              // so both fetches below bail out with a name='AbortError'
              // — preventing a stale dispatch from clobbering the new turn.
              const turnAbort = new AbortController();
              fillerRef.current.abortCtrl = turnAbort;
              const turnSignal = turnAbort.signal;

              const clarPromise = fetch('/api/voice/clarification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload),
                signal: turnSignal,
              }).then((r) => (r.ok ? r.json() : { clarification: null })).catch(() => ({ clarification: null }));
              // Stream the agent-query via SSE so we can show progress in the
              // debug log as soon as the model emits partial text. The agent's
              // Quick workflow emits `partial` events whose text is cumulative
              // within a model turn — first turn is the "I'll look up that..."
              // narration, then a tool call fires, then a new turn starts with
              // the actual answer. We currently speak only the FINAL `result`
              // event because (a) partials are cumulative + pre-tool narration
              // would create an awkward gap before the real answer arrives, and
              // (b) OpenAI Realtime's function_call_output is one-shot, not a
              // streaming target. The streaming endpoint still helps because we
              // log partials to the debug panel so the user can see the agent
              // is making progress instead of staring at an idle orb.
              const agentPromise = (async () => {
                try {
                  const r = await fetch('/api/voice/agent-query', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Accept: 'text/event-stream',
                    },
                    credentials: 'include',
                    body: JSON.stringify(payload),
                    signal: turnSignal,
                  });
                  if (!r.ok || !r.body) throw new Error(`agent-query http ${r.status}`);
                  const reader = r.body.getReader();
                  const decoder = new TextDecoder();
                  let buf = '';
                  let finalAnswer = '';
                  let lastPartialLen = 0;
                  while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buf += decoder.decode(value, { stream: true });
                    const frames = buf.split('\n\n');
                    buf = frames.pop() || '';
                    for (const frame of frames) {
                      let evt = '';
                      let data = '';
                      for (const line of frame.split('\n')) {
                        if (line.startsWith('event: ')) evt = line.slice(7).trim();
                        else if (line.startsWith('data: ')) data += line.slice(6);
                      }
                      if (!data) continue;
                      let payloadObj;
                      try { payloadObj = JSON.parse(data); } catch (_) { continue; }
                      if (evt === 'partial' && typeof payloadObj.text === 'string') {
                        const f = fillerRef.current;
                        // Backend emits `{text:'', clear:true}` at turn
                        // boundaries (between model + tool turns). Reset
                        // the accumulator so we don't speak across
                        // boundaries — but DON'T un-set f.spoken; filler
                        // is once-per-user-turn, not once-per-model-turn.
                        if (payloadObj.clear) {
                          f.partial = '';
                          if (f.stableTimer) { clearTimeout(f.stableTimer); f.stableTimer = null; }
                          continue;
                        }
                        const partialText = payloadObj.text;
                        // Debug-log only on growth (cumulative semantics —
                        // don't spam the panel with identical repeats).
                        if (partialText.length > lastPartialLen) {
                          lastPartialLen = partialText.length;
                          debugLog_(`📝 partial: "${partialText.slice(-80)}"`, 'meta');
                        }
                        f.partial = partialText;
                        // Stability check: if partials keep growing within
                        // 500ms the timer resets. Once the partial has
                        // stopped growing for 500ms AND we've committed to
                        // the agent path AND the real answer isn't already
                        // in hand, speak it as filler.
                        if (f.stableTimer) clearTimeout(f.stableTimer);
                        f.stableTimer = setTimeout(() => {
                          f.stableTimer = null;
                          maybeSpeakFiller();
                        }, 500);
                      } else if (evt === 'result' && typeof payloadObj.answer === 'string') {
                        finalAnswer = payloadObj.answer;
                        const f = fillerRef.current;
                        // Mark finalReady so any still-armed stability
                        // timer skips filler (race: timer fires AFTER we
                        // captured result but BEFORE the dispatch path).
                        f.finalReady = true;
                        if (f.stableTimer) { clearTimeout(f.stableTimer); f.stableTimer = null; }
                      } else if (evt === 'error') {
                        console.warn('[VoiceModeOverlay] agent-query stream error:', payloadObj);
                      }
                    }
                  }
                  return { answer: finalAnswer || 'Sorry, the agent did not produce a final answer.' };
                } catch (err) {
                  console.warn('[VoiceModeOverlay] agent-query stream failed:', err?.message);
                  return { answer: 'I could not reach the agent backend.' };
                }
              })();

              // 1. Wait only for clarification (~500ms). agent-query keeps running.
              const clarResp = await clarPromise;
              const clarification = (clarResp?.clarification || '').trim();
              const useClarification = clarification && !justAskedClarificationRef.current;

              let speakText;
              let viaHint;

              if (useClarification) {
                // Speak the clarification immediately, while agent-query is
                // still running. Store the agent promise so the next user
                // utterance (their reply) can pick up the cached analysis.
                speakText = clarification;
                viaHint = 'clarification (fast path)';
                pendingAgentPromiseRef.current = agentPromise;
                debugLog_(`✋ clarification: "${clarification}" (agent-query continuing in background)`, 'agent');
                justAskedClarificationRef.current = true;
                // Cache that this turn the spoken assistant turn was the
                // clarification. Once the agent-query completes, we update
                // history asynchronously so the next call has the analysis.
                agentPromise.then((agentResp) => {
                  const fullAnswer = (agentResp?.answer || '').trim() || 'No answer available.';
                  debugLog_(`📦 agent-query finished in background: "${fullAnswer.slice(0, 70)}${fullAnswer.length > 70 ? '…' : ''}"`, 'meta');
                  // Append the preliminary analysis as a SEPARATE assistant
                  // history entry so when the user's clarification reply
                  // triggers the next call, the prior analysis is right
                  // there in history.
                  historyRef.current = [
                    ...historyRef.current,
                    {
                      role: 'assistant',
                      content: `(Preliminary analysis computed while user replies to my clarification: ${fullAnswer})`,
                    },
                  ].slice(-12);
                });
              } else {
                // No clarification needed — commit to the agent path. From
                // this point the SSE partial handler is allowed to dispatch
                // a stable partial as filler narration. We also probe
                // maybeSpeakFiller() once here in case a stable partial
                // already exists at the moment clarification resolved.
                fillerRef.current.committed = true;
                try { maybeSpeakFiller(); } catch (_) { /* noop */ }
                const agentResp = await agentPromise;
                speakText = (agentResp?.answer || '').trim() || 'No answer available.';
                viaHint = agentResp?.via || 'one-shot-claude';
                debugLog_(`✅ agent → "${speakText.slice(0, 100)}${speakText.length > 100 ? '…' : ''}"`, 'agent');
                justAskedClarificationRef.current = false;
              }

              debugLog_(`🛠 path: ${viaHint}`, 'meta');

              // Record assistant turn in history (what we spoke).
              historyRef.current = [
                ...historyRef.current,
                { role: 'assistant', content: speakText },
              ].slice(-12);

              setStatus('speaking');

              // Cancel any still-armed filler timer — the real answer is
              // about to be spoken, no point firing a late filler.
              {
                const f = fillerRef.current;
                if (f.stableTimer) { clearTimeout(f.stableTimer); f.stableTimer = null; }
              }

              // DISPATCH CHOICE:
              //   viaCall=true  → filler already consumed function_call_output
              //                   for this call_id. Final answer must go
              //                   through instructions-only response.create —
              //                   sending another function_call_output for
              //                   the same call_id errors.
              //                   (Note: conversation.item.create with
              //                   role='assistant' was tried and rejected by
              //                   the model — it treats injected assistant
              //                   content as something it already said and
              //                   just appends "Let me know if you need more
              //                   details", dropping the actual numbers.)
              //   viaCall=false → original path: function_call_output +
              //                   response.create. Either no filler spoke,
              //                   OR clarification won the race.
              //
              // RACE GUARD: Realtime rejects a second response.create while
              // the first is still active. If filler is mid-speech
              // (fillerRef.active), queue the dispatch and let the
              // response.done handler drain it.
              const dispatchFinal = () => {
                try {
                  if (fillerRef.current.viaCall) {
                    dc.send(JSON.stringify({
                      type: 'response.create',
                      response: {
                        tool_choice: 'none',
                        instructions:
                          'Deliver the following answer to the user as ONE concise spoken response. Lead DIRECTLY with the headline number or finding — do NOT preface with "Here is what I found", "Perfect", "Here\'s the answer in spoken style", or any other lead-in. Stay under 2 short sentences. Do NOT call any tool. Do NOT abbreviate the numbers. Paraphrase markdown emphasis (** ... **) as natural vocal emphasis. Stop immediately after the headline + at most one assumption clause.\n\n--- ANSWER TO SPEAK ---\n' + speakText,
                      },
                    }));
                  } else {
                    dc.send(JSON.stringify({
                      type: 'conversation.item.create',
                      item: {
                        type: 'function_call_output',
                        call_id: id,
                        output: JSON.stringify({ answer: speakText }),
                      },
                    }));
                    dc.send(JSON.stringify({
                      type: 'response.create',
                      response: {
                        tool_choice: 'none',
                        instructions: useClarification
                          ? 'Speak the function_call_output `answer` field VERBATIM as a brief clarifying question. Do NOT add commentary, do NOT call any tool.'
                          : 'Speak the function_call_output `answer` field as ONE concise spoken response. Lead DIRECTLY with the headline number/finding — do NOT preface with "Here is what I found", "Perfect", or any lead-in. Do NOT call any tool. Stay under 2 short sentences; paraphrase numbers and code references naturally.',
                      },
                    }));
                  }
                } catch (sendErr) {
                  console.warn('[VoiceModeOverlay] failed to push function output:', sendErr?.message);
                }
              };

              // Stale-turn guard. If a NEW user turn has fired since this
              // one started, resetFillerState aborted our fetches AND
              // wiped fillerRef.callId. Skip the dispatch entirely — the
              // user has moved on, and dispatching now would either
              // double-speak or race with the new turn's response.
              if (turnSignal.aborted || fillerRef.current.callId !== id) {
                debugLog_(`⤬ dropping stale answer for call_id=${id} (user moved on)`, 'meta');
              } else if (activeResponseIdRef.current) {
                // ANY response active (filler we dispatched OR a model
                // auto-response from a Whisper-segmented follow-up
                // utterance) blocks our final dispatch. Queue it; the
                // response.done handler drains the queue.
                debugLog_('⏳ response active; queueing final answer', 'meta');
                fillerRef.current.pendingDispatch = dispatchFinal;
              } else {
                dispatchFinal();
              }
            } catch (fetchErr) {
              console.warn('[VoiceModeOverlay] agent-query fetch failed:', fetchErr?.message);
              debugLog_(`✗ agent-query fetch failed: ${fetchErr?.message}`, 'warn');
              try {
                dc.send(JSON.stringify({
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: id,
                    output: JSON.stringify({ answer: 'I could not reach the agent backend.' }),
                  },
                }));
                dc.send(JSON.stringify({
                  type: 'response.create',
                  response: {
                    tool_choice: 'none',
                    instructions: 'Speak the function_call_output `answer` field to the user. Do not call any tool.',
                  },
                }));
              } catch (_) { /* noop */ }
            }
          })();
          return;
        }

        // Diagnostic: a response COMPLETED with no function call means the
        // model decided to answer from its own knowledge. Surface this both
        // to the console AND the on-screen debug log so prompt-tuning
        // regressions are obvious without DevTools.
        if (t === 'response.done') {
          const out = event.response?.output || [];
          const hadTool = out.some((o) => o.type === 'function_call');
          // Read the LIVE badge value from the ref, not the stale closure
          // variable. Without this, the model's spoken response that comes
          // AFTER a tool call (which is naturally output=[message]) gets
          // mis-flagged as "OpenAI direct".
          const liveBadge = routeBadgeRef.current;
          if (!hadTool && liveBadge !== 'agent') {
            console.warn(
              '[VoiceModeOverlay] ⚠️ response.done with NO function call — model answered without calling ask_jobotics_agent.',
              'Output types:', out.map((o) => o.type)
            );
            setRouteBadgeSynced('direct');
            debugLog_(`⚠️ OpenAI answered directly (no agent call). output=[${out.map((o) => o.type).join(',')}]`, 'warn');
          } else if (hadTool) {
            // We already badged 'agent' when we dispatched the function call.
            debugLog_(`↩︎ response.done (function_call emitted)`, 'meta');
          } else {
            // hadTool=false but badge already 'agent' — this is the second
            // response.done that fires AFTER the tool returns and the model
            // speaks the agent's answer. That's correct behavior.
            debugLog_(`↩︎ response.done (agent answer spoken)`, 'agent');
          }
          // ANY response.done ends the active response — including
          // function_call response.done, after which the conversation is
          // "between" responses and the API will accept the next
          // response.create. (Earlier version only cleared on !hadTool
          // and the queued final-answer dispatch never drained when the
          // blocking response was a function_call from a Whisper-
          // segmented echo utterance.)
          activeResponseIdRef.current = null;

          const f = fillerRef.current;
          // Clear filler hush only when filler itself ended (a spoken,
          // non-tool response we initiated). A function_call response.done
          // shouldn't un-hush mid-filler.
          if (!hadTool && f.active) {
            f.active = false;
            setHushDuringFiller(false);
          }
          // Drain queued final-answer dispatch on ANY response.done.
          const pending = f.pendingDispatch;
          if (pending) {
            f.pendingDispatch = null;
            debugLog_('▶ response ended; dispatching queued final answer', 'meta');
            try { pending(); } catch (e) { console.warn('[VoiceModeOverlay] queued dispatch threw:', e?.message); }
          }
          return;
        }

        if (t === 'error') {
          console.warn('[VoiceModeOverlay] realtime error:', event.error || event);
          debugLog_(`✗ realtime error: ${JSON.stringify(event.error || event).slice(0, 200)}`, 'warn');
        }
      });

      // 3. SDP exchange.
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const sdpRes = await fetch(
        `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(model)}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${clientSecret}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        }
      );
      if (!sdpRes.ok) {
        const body = await sdpRes.text();
        throw new Error(`sdp http ${sdpRes.status}: ${body.slice(0, 200)}`);
      }
      const answerSdp = await sdpRes.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      setStatus('listening');
    } catch (err) {
      console.warn('[VoiceModeOverlay] connect failed:', err?.message);
      setErrorMsg(err?.message || 'Connection failed');
      setStatus('error');
      tearDown();
    }
  }, [agentId, tearDown]);

  // Open / close lifecycle.
  useEffect(() => {
    if (!open) return undefined;
    connect();
    return () => tearDown();
  }, [open, connect, tearDown]);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Half-duplex mic policy. The mic is hushed whenever:
  //   - user explicitly muted, OR
  //   - we're in the 2.8s post-connect window (chirp + greeting tail), OR
  //   - the filler narration is speaking (we set this explicitly), OR
  //   - the MODEL is currently speaking any response (status==='speaking').
  //
  // Rationale: on iPhone speakerphone (and some laptop speakers) browser
  // AEC isn't strong enough to suppress the model's own voice from
  // bleeding into the mic. Whisper transcribes the bleed as a user
  // utterance, and the session-level tool_choice:required then FORCES
  // the model to call a tool — sometimes inventing a fake user question
  // from the previous assistant utterance ("Bye." → "I'm here to help
  // with labor estimates."). That's a hard demo blocker.
  //
  // Tradeoff: barge-in via voice during AI speech is disabled. User can
  // still interrupt by tapping the mute button (which un-hushes via
  // !muted being false ironically… actually we use the muted state as
  // user-mute, so this only kicks in mid-speech; tapping the close
  // button kills everything).
  useEffect(() => {
    const stream = micStreamRef.current;
    if (!stream) return;
    const enabled = !muted && !hushOnConnect && !hushDuringFiller && status !== 'speaking';
    stream.getAudioTracks().forEach((t) => { t.enabled = enabled; });
  }, [muted, hushOnConnect, hushDuringFiller, status]);

  // Trim playback volume while the model is speaking. ~3dB cut roughly
  // halves the echo amplitude reaching the bottom mic on iPhone
  // speakerphone — small enough to still be clearly audible, but enough
  // to put residual playback well below the semantic VAD's threshold.
  useEffect(() => {
    const audio = remoteAudioRef.current;
    if (!audio) return;
    audio.volume = status === 'speaking' ? 0.72 : 1.0;
  }, [status]);

  // Orb scale animation: when user is speaking we pulse subtly; when model is
  // speaking we scale by RMS of its audio.
  const orbScale = useMemo(() => {
    if (status === 'speaking') return 1 + Math.min(0.45, outputLevel * 3);
    if (status === 'listening') return 1.03;
    return 1;
  }, [status, outputLevel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-sm text-white flex flex-col">
      <header className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-widest text-slate-400">Voice mode</span>
          <span className="text-sm font-medium text-slate-200">{agentName || 'Jobotics Agent'}</span>
          {/* Routing badges removed from the header — they leaked an
              implementation detail (which backend handled this turn) and
              cluttered the demo UI. Routing is still tracked in routeBadge
              state and surfaced in the Debug panel when toggled on. */}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDebugOpen((v) => !v)}
            aria-pressed={debugOpen}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              debugOpen
                ? 'bg-slate-700 border-slate-600 text-slate-100'
                : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200'
            }`}
          >
            Debug {debugOpen ? 'on' : 'off'}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close voice mode"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800 hover:bg-slate-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-8 gap-8 min-h-0">
        {/* Assistant transcript — large, streaming text the user can read
            along with the spoken audio. Updates via response.audio_transcript
            .delta events on the data channel, so visible text matches what
            the voice is saying in real time. A faint blinking caret marks
            the current position while the model is still speaking. */}
        <div className="w-full max-w-3xl min-h-[6rem] text-center text-2xl sm:text-3xl text-slate-50 leading-relaxed font-light">
          {assistantTranscript ? (
            <>
              {assistantTranscript}
              {status === 'speaking' && (
                <span className="inline-block ml-1 w-[3px] h-7 align-middle bg-slate-200 animate-pulse" aria-hidden />
              )}
            </>
          ) : (
            <span className="text-slate-500 text-base">
              {status === 'connecting' && 'Connecting…'}
              {status === 'listening' && (hushOnConnect ? 'Warming up — mic opens in a moment' : 'Listening — speak when ready')}
              {status === 'idle' && '…'}
              {status === 'error' && (errorMsg || 'Voice connection error')}
            </span>
          )}
        </div>

        {/* Orb */}
        <div className="relative">
          <div
            className={`w-48 h-48 rounded-full transition-transform duration-150 ease-out shadow-2xl
              ${status === 'speaking' ? 'bg-gradient-to-br from-indigo-500 to-purple-700' : ''}
              ${status === 'listening' ? 'bg-gradient-to-br from-teal-500 to-emerald-700' : ''}
              ${status === 'connecting' ? 'bg-gradient-to-br from-slate-700 to-slate-900 animate-pulse' : ''}
              ${status === 'idle' ? 'bg-gradient-to-br from-slate-700 to-slate-900' : ''}
              ${status === 'error' ? 'bg-gradient-to-br from-red-600 to-rose-900' : ''}
            `}
            style={{ transform: `scale(${orbScale})` }}
          />
          <div className="absolute inset-0 rounded-full ring-2 ring-white/10 pointer-events-none" />
        </div>

        {/* User subtitle (bottom) — what the user is saying right now.
            Smaller than the assistant transcript so the visual hierarchy
            mirrors the conversational hierarchy (assistant = primary read). */}
        <div className="w-full max-w-2xl min-h-[3rem] text-center text-lg text-slate-300 italic leading-snug">
          {userTranscript || (status === 'listening' ? <span className="text-slate-500">…</span> : '')}
        </div>

        {/* On-screen debug log — shows each turn's routing path so you can
            tell at a glance whether voice mode called the Jobotics Agent or
            answered directly from OpenAI. Color-coded:
              meta (slate) — speech start, response.done routed via agent
              user (sky)   — finalized user transcript
              agent (emerald) — function-call lifecycle + agent answer
              warn (amber)  — OpenAI direct, errors */}
        {debugOpen && (
          <div className="w-full max-w-3xl bg-slate-900/80 border border-slate-700 rounded-lg p-3 text-xs font-mono overflow-y-auto max-h-44 shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              Debug log (newest at bottom) — agent: {agentId || '(generic)'}
            </div>
            {debugLog.length === 0 ? (
              <div className="text-slate-500">…waiting for events…</div>
            ) : (
              debugLog.map((row) => (
                <div
                  key={row.id}
                  className={
                    row.kind === 'agent'
                      ? 'text-emerald-300'
                      : row.kind === 'user'
                        ? 'text-sky-300'
                        : row.kind === 'warn'
                          ? 'text-amber-300'
                          : 'text-slate-400'
                  }
                >
                  <span className="text-slate-500">{row.ts}</span> {row.msg}
                </div>
              ))
            )}
          </div>
        )}
      </main>

      <footer className="px-6 pb-8 pt-2 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => setMuted((m) => !m)}
          title={muted ? 'Unmute mic' : 'Mute mic'}
          className={`w-12 h-12 flex items-center justify-center rounded-full transition-colors ${
            muted ? 'bg-red-600 hover:bg-red-500' : 'bg-slate-800 hover:bg-slate-700'
          }`}
        >
          {muted ? <MicOff size={20} /> : <Mic size={20} />}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-sm"
        >
          End voice mode
        </button>
        {/* Hidden audio sink for the model's remote audio track. */}
        <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />
      </footer>
    </div>
  );
}
